import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Users, Kanban, FileText, CheckSquare, MessageSquare, Package, Settings, LogOut, Sparkles, PanelLeftClose, PanelLeft, Factory, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { mediaUrl } from "@/lib/api";
import { useState, useEffect } from "react";
import AIAssistant from "@/components/AIAssistant";

const NAV = [
  { to: "/app", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "sb-dashboard" },
  { to: "/app/funil", label: "Funil", icon: Kanban, testid: "sb-funil" },
  { to: "/app/producao", label: "Produção", icon: Factory, testid: "sb-producao" },
  { to: "/app/clientes", label: "Clientes", icon: Users, testid: "sb-clientes" },
  { to: "/app/orcamentos", label: "Orçamentos", icon: FileText, testid: "sb-orcamentos" },
  { to: "/app/tarefas", label: "Tarefas", icon: CheckSquare, testid: "sb-tarefas" },
  { to: "/app/conversas", label: "Conversas", icon: MessageSquare, testid: "sb-conversas" },
  { to: "/app/produtos", label: "Produtos", icon: Package, testid: "sb-produtos" },
  { to: "/app/configuracoes", label: "Configurações", icon: Settings, testid: "sb-configuracoes" },
];

// 5 itens mais usados na barra inferior do celular
const BOTTOM = ["/app/funil", "/app/conversas", "/app/clientes", "/app/tarefas", "/app/producao"];
const bottomNav = BOTTOM.map((to) => NAV.find((n) => n.to === to));
const moreNav = NAV.filter((n) => !BOTTOM.includes(n.to));

export default function CrmLayout() {
  const { user, logout } = useAuth();
  const [aiOpen, setAiOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const nav = useNavigate();
  useEffect(() => { document.title = "ATP Gestão 360°"; }, []);

  const bottomCls = ({ isActive }) =>
    `flex flex-col items-center justify-center gap-0.5 flex-1 py-1.5 text-[10px] font-medium ${isActive ? "text-[#0CDBF2]" : "text-slate-400"}`;

  return (
    <div className="atp-dark flex h-screen overflow-hidden bg-[#07090E] text-slate-100">
      {/* Sidebar (desktop) */}
      <aside className={`hidden md:flex ${collapsed ? "w-16" : "w-64"} bg-[#0B0F17] border-r border-[#1E293B] flex-col shrink-0 transition-all duration-200`}>
        <div className="h-16 flex items-center gap-2 px-4 border-b border-[#1E293B]">
          {collapsed
            ? <div className="w-8 h-8 rounded-md bg-[#0CDBF2] flex items-center justify-center font-heading font-black text-[#07090E] shrink-0">A</div>
            : <img src="/atp-logo.webp" alt="ATP Confecções" className="h-9 w-auto object-contain" data-testid="crm-logo" />}
          <button onClick={() => setCollapsed(!collapsed)} className="ml-auto text-slate-400 hover:text-[#0CDBF2]" data-testid="sidebar-toggle">{collapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}</button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid} title={n.label}
              className={({isActive}) =>
                `flex items-center gap-3 ${collapsed ? "px-0 justify-center" : "px-5"} py-2.5 text-sm font-medium border-l-2 ${isActive ? "bg-[#0CDBF2]/5 text-[#0CDBF2] border-[#0CDBF2]" : "text-slate-400 hover:text-white border-transparent hover:bg-white/[0.02]"}`}>
              <n.icon size={16} />
              {!collapsed && <span>{n.label}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#1E293B]">
          {!collapsed && (
            <div className="mb-3 flex items-center gap-3">
              {user?.avatar_url
                ? <img src={mediaUrl(user.avatar_url)} alt={user?.name} className="w-9 h-9 rounded-full object-cover border border-[#1E293B] shrink-0" data-testid="sb-avatar" />
                : <div className="w-9 h-9 rounded-full bg-[#0CDBF2]/20 text-[#0CDBF2] flex items-center justify-center text-sm font-semibold shrink-0">{(user?.name||"?").slice(0,1)}</div>}
              <div className="min-w-0">
                <div className="text-sm font-medium truncate" data-testid="sb-current-user">{user?.name}</div>
                <div className="text-xs text-slate-500 truncate">{user?.role}</div>
              </div>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={logout}
            className="w-full border-slate-700 hover:bg-slate-800 text-slate-200" data-testid="sb-logout">
            <LogOut size={14} className={collapsed ? "" : "mr-2"} /> {!collapsed && "Sair"}
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 border-b border-[#1E293B] bg-[#0B0F17]/70 backdrop-blur flex items-center justify-between px-4 sm:px-6 shrink-0">
          <img src="/atp-logo.webp" alt="ATP Confecções" className="h-8 w-auto object-contain md:hidden" />
          <div className="hidden md:block text-sm text-slate-400">
            <span className="font-heading font-semibold text-slate-100">ATP Confecções</span> · CRM Comercial
          </div>
          <Button size="sm" onClick={() => setAiOpen(true)}
            className="bg-[#0CDBF2]/10 hover:bg-[#0CDBF2]/20 text-[#0CDBF2] border border-[#0CDBF2]/30"
            data-testid="open-ai-assistant">
            <Sparkles size={14} className="mr-2" /> Copiloto IA
          </Button>
        </header>
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>

        {/* Bottom nav (mobile) */}
        <nav className="md:hidden shrink-0 border-t border-[#1E293B] bg-[#0B0F17] flex items-stretch pb-[env(safe-area-inset-bottom)]" data-testid="mobile-bottom-nav">
          {bottomNav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`bn-${n.testid}`} className={bottomCls}>
              <n.icon size={19} />
              <span>{n.label}</span>
            </NavLink>
          ))}
          <button onClick={() => setMoreOpen(true)} data-testid="bn-more"
            className="flex flex-col items-center justify-center gap-0.5 flex-1 py-1.5 text-[10px] font-medium text-slate-400">
            <MoreHorizontal size={19} />
            <span>Mais</span>
          </button>
        </nav>
      </div>

      {/* "Mais" drawer (mobile) */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="bg-[#0B0F17] border-t border-[#1E293B] text-slate-100 rounded-t-2xl pb-[env(safe-area-inset-bottom)]">
          <SheetHeader><SheetTitle className="text-slate-100 text-left">Mais</SheetTitle></SheetHeader>
          <div className="grid grid-cols-2 gap-2 mt-3">
            {moreNav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMoreOpen(false)} data-testid={`more-${n.testid}`}
                className={({isActive}) => `flex items-center gap-3 rounded-xl border p-3.5 text-sm font-medium ${isActive ? "border-[#0CDBF2] text-[#0CDBF2] bg-[#0CDBF2]/5" : "border-[#1E293B] text-slate-200"}`}>
                <n.icon size={18} /> {n.label}
              </NavLink>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-3 border-t border-[#1E293B] pt-4">
            {user?.avatar_url
              ? <img src={mediaUrl(user.avatar_url)} alt={user?.name} className="w-10 h-10 rounded-full object-cover border border-[#1E293B]" />
              : <div className="w-10 h-10 rounded-full bg-[#0CDBF2]/20 text-[#0CDBF2] flex items-center justify-center text-sm font-semibold">{(user?.name||"?").slice(0,1)}</div>}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.role}</div>
            </div>
            <Button variant="outline" size="sm" onClick={() => { setMoreOpen(false); logout(); }}
              className="border-slate-700 hover:bg-slate-800 text-slate-200" data-testid="more-logout">
              <LogOut size={14} className="mr-2" /> Sair
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <AIAssistant open={aiOpen} onClose={() => setAiOpen(false)} />
    </div>
  );
}
